import { useEffect, useRef } from 'react'

import type { AgentCommand } from '../../lib/api/admin.ts'
import { describeError } from '../../lib/api/messages.ts'
import { isAwaitingAgent } from '../../lib/commands/semantics.ts'
import { useCommandTracking } from '../../lib/commands/useCommandTracking.ts'
import type { WebOutcome } from '../../lib/webdefense/semantics.ts'
import { ReleaseStatus } from '../commands/CommandState.tsx'
import { Button } from '../primitives/Button.tsx'
import { ErrorState, LoadingRow, Notice } from '../primitives/States.tsx'
import { useToast } from '../primitives/Toast.tsx'

/**
 * Acompanha UM comando do Web Defense por `GET /commands/{id}` até o agente
 * responder. Como no firewall de IP, o 202 é "na fila": a frase de sucesso só
 * aparece em `executed`/`skipped`. `onSettled` avisa quem chama quando a
 * resposta mudou o estado no Athena — o que inclui o `failed` do resolve.
 */
export function WebCommandTracking({
  commandId,
  initial,
  outcomeOf,
  toastLabel,
  onSettled,
  onClose,
}: {
  commandId: string
  initial: AgentCommand | null
  /** A frase que a tela pode afirmar em cada estado (lib/webdefense/semantics.ts). */
  outcomeOf: (state: string) => WebOutcome
  /** Prefixo do aviso de confirmação: a máquina ou o IP. */
  toastLabel: string
  onSettled: (outcome: WebOutcome) => void
  onClose: () => void
}) {
  const toast = useToast()
  const { command, error, refresh } = useCommandTracking(commandId, initial)
  const announced = useRef(false)
  // Só anuncia a confirmação que aconteceu diante do operador.
  const sawAwaiting = useRef(false)

  const state = command?.state ?? null
  if (state !== null && isAwaitingAgent(state)) sawAwaiting.current = true
  const outcome = state === null ? null : outcomeOf(state)
  const confirmed = outcome?.confirmed === true
  const settled = outcome?.settled === true

  useEffect(() => {
    if (!settled || outcome === null || announced.current) return
    announced.current = true
    onSettled(outcome)
    if (confirmed && sawAwaiting.current) toast.success(`${toastLabel}: ${outcome.headline}`)
    // `announced` evita anunciar duas vezes; o resto é estável por resposta.
  }, [settled])

  return (
    <div className="space-y-3">
      {command === null || outcome === null ? (
        error !== null ? (
          <ErrorState error={error} onRetry={refresh} />
        ) : (
          <LoadingRow label="Lendo o comando…" />
        )
      ) : (
        <>
          <ReleaseStatus command={command} outcome={outcome} />
          {error !== null && (
            <Notice tone="warn">
              Não foi possível atualizar o estado: {describeError(error)}{' '}
              <button type="button" className="underline" onClick={refresh}>
                Tentar de novo
              </button>
            </Notice>
          )}
        </>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-line-subtle pt-3">
        <span className="mr-auto font-mono text-micro text-fg-muted">comando {commandId}</span>
        <Button variant={settled ? 'primary' : 'ghost'} onClick={onClose}>
          Fechar
        </Button>
      </div>
    </div>
  )
}
